import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaSave, FaArchive, FaUndo, FaPlus } from 'react-icons/fa';
import WikiTreeView from '../../../components/WikiTreeView';
import TmeWikiPageEditor from '../../../components/tmeWiki/TmeWikiPageEditor';
import TagInput, { normaliseTag } from '../../../components/tmeWiki/TagInput';
import VersionHistoryPanel from '../../../components/tmeWiki/VersionHistoryPanel';
import TmeWikiLinksPanel from './TmeWikiLinksPanel';
import StaleContentBanner from '../../../components/StaleContentBanner';
import { apiFetch } from '../../../utils/apiAdmin';
import { adminSessionUsable } from '../../../utils/authAdmin';
import { sanitiseWikiHtml } from '../../../utils/wikiSanitise';
import {
    adminSub, saveDraft, readDraft, clearDraft, draftIsNewerThan
} from '../../../utils/tmeWikiDrafts';
import { ensureTmeWikiMediaCookies } from '../../../utils/tmeWikiMediaCookies';

/**
 * One wiki: its page tree, the page editor, and the archived state.
 *
 * Planning/TME-WIKI-2026-09-02.md §7.1, §9.5.1, §10.1, §10.3, §10.4.
 *
 * ⚠️ THE READ PATH SANITISES TOO (§7.1). `admin-wiki` sanitises on write only,
 * and the client render sanitises again with the frontend copy - for the same
 * reason: stored HTML predates any future allow-list change, so what is rendered
 * is purified at the moment it is rendered, not at the moment it was saved.
 *
 * ⚠️ AN ARCHIVED PAGE IS OPENED, NOT REFUSED (§9.5.1). A deep link resolves
 * rather than 404ing, and the page renders READ-ONLY under a banner with a
 * restore control - which is also the only place an archived page can be
 * brought back from.
 */

const API = `${process.env.REACT_APP_API_URL}/admin/tme-wiki`;

/** 25 MB (D24). The server pins it in the signature; this is the early refusal. */
const MAX_UPLOAD_BYTES = 26214400;

/** How long after the last title keystroke the draft is written. Matches the editor's own debounce. */
export const TITLE_DRAFT_DEBOUNCE_MS = 1500;

const TmeWikiEditorPane = ({
    wikiId, wiki, initialPageId, tagFilter = [], onTagFilterChange,
    scripts = [], onRefreshScripts, onUnsavedChanges, liveMessages = [], onPageSelect
}) => {
    const [pages, setPages] = useState([]);
    const [showArchivedPages, setShowArchivedPages] = useState(false);
    const [selectedPageId, setSelectedPageId] = useState(initialPageId || null);
    const [page, setPage] = useState(null);
    const [title, setTitle] = useState('');
    const [tags, setTags] = useState([]);
    const [tagSuggestions, setTagSuggestions] = useState([]);
    const [contentHtml, setContentHtml] = useState('');
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [pendingDraft, setPendingDraft] = useState(null);
    // TWM §7.3.1 - the lock over the whole delete flow: it hides the toolbar,
    // flips editability, disables the stale banner's Reload, makes the version
    // panel read-only and holds the live-refresh drain. Each of those is a way
    // the open document could otherwise be replaced between the flow's save and
    // its DELETE, which would leave a live page pointing at deleted bytes.
    const [deletingMedia, setDeletingMedia] = useState(false);
    // ⚠️ A REF as well, for the re-entry refusal: the node's callback is stable
    // (§7.1.1) and must not read a captured render's state value.
    const deletingMediaRef = useRef(false);

    const subRef = useRef(adminSub());
    const editorRef = useRef(null);
    // The body as last LOADED or last SENT (TWS §4.1). The editor debounces
    // its onChange, so one can fire after a save carrying the html just
    // saved; without this it re-marked the page dirty and re-wrote a draft
    // newer than the page, which the next open then offered back as a
    // "draft" identical to the page.
    const savedHtmlRef = useRef('');

    // The links panel's picker is controlled here, because the editor's
    // inline-insert buttons open the SAME one and the row must be written
    // before the node is inserted (§9.3, H4).
    const [picker, setPicker] = useState(null);
    const [insertOnLink, setInsertOnLink] = useState(false);
    const [linksExpanded, setLinksExpanded] = useState(
        () => localStorage.getItem('tmeWikiLinksExpanded') === 'true'
    );
    const [versionsExpanded, setVersionsExpanded] = useState(
        () => localStorage.getItem('tmeWikiVersionsExpanded') === 'true'
    );

    useEffect(() => {
        localStorage.setItem('tmeWikiLinksExpanded', linksExpanded.toString());
    }, [linksExpanded]);

    useEffect(() => {
        localStorage.setItem('tmeWikiVersionsExpanded', versionsExpanded.toString());
    }, [versionsExpanded]);

    useEffect(() => {
        onUnsavedChanges?.(dirty);
    }, [dirty, onUnsavedChanges]);

    // ⚠️ SILENT MODE (LIV, D1). The mount path blanks the TREE and raises
    // `loadError` on any failure; a refresh nobody asked for must do neither.
    const fetchPages = useCallback(async ({ silent = false } = {}) => {
        try {
            const response = await apiFetch(`${API}/wikis/${wikiId}/pages`);
            if (!response.ok) throw new Error(`Failed to load pages (${response.status})`);
            const data = await response.json();
            setPages(data.pages || []);
        } catch (err) {
            console.error('Failed to load TME wiki pages:', err);
            if (silent) return;
            setLoadError(err.message);
            setPages([]);
        }
    }, [wikiId]);

    useEffect(() => {
        fetchPages();
    }, [fetchPages]);

    /** The tag registry, for autocomplete and for the filter. One keyed Query. */
    const fetchTags = useCallback(async () => {
        try {
            const response = await apiFetch(`${API}/tags`);
            if (!response.ok) return;
            const data = await response.json();
            setTagSuggestions(data.tags || []);
        } catch (err) {
            console.error('Failed to load TME wiki tags:', err);
        }
    }, []);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    // Signing is enforced on /tme-wiki-media/*, so the three CloudFront cookies
    // have to be in place before any <img> is fetched (§5.4). ONE set covers
    // every internal wiki (D19), so this takes no arguments and runs once.
    useEffect(() => {
        ensureTmeWikiMediaCookies();
    }, []);

    /**
     * Presign, PUT, and hand back the stored path.
     *
     * ⚠️ THE SIZE IS REFUSED HERE **AND** SIGNED THERE (D24). A presigned URL
     * cannot impose a maximum, so the server pins `ContentLength` and S3 rejects
     * a body of any other length; this check only saves a pointless round trip
     * and gives the reader a better message than S3's.
     *
     * ⚠️ `Content-Disposition` MUST BE SENT WHEN IT WAS SIGNED, byte for byte,
     * or S3 rejects the signature. The client wiki's upload sends `Content-Type`
     * only, so this is a difference implemented deliberately rather than
     * inherited.
     */
    const uploadMedia = async (file) => {
        if (file.size > MAX_UPLOAD_BYTES) {
            window.alert(`That file is ${Math.round(file.size / 1048576)} MB. The limit is 25 MB.`);
            return null;
        }

        // A .log file has no MIME type in most browsers; the plan stores it as
        // text/plain (§5.7). Anything else unrecognised is sent as-is and
        // refused by the server's allow-list, which is where that decision lives.
        const isLog = /\.log$/i.test(file.name);
        const contentType = file.type || (isLog ? 'text/plain' : '');

        const presign = await apiFetch(`${API}/media/upload-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                wikiId, filename: file.name, contentType, sizeBytes: file.size
            })
        });
        const data = await presign.json();
        if (!presign.ok) {
            // The refusal names the Script Repository for a script type, and the
            // limit for an oversize one. Show the server's own words.
            window.alert(data.message || 'Could not upload that file');
            return null;
        }

        const headers = { 'Content-Type': data.contentType };
        if (data.contentDisposition) headers['Content-Disposition'] = data.contentDisposition;

        const put = await fetch(data.uploadUrl, { method: 'PUT', headers, body: file });
        if (!put.ok) {
            window.alert(`The upload was refused by storage (${put.status})`);
            return null;
        }
        return { url: data.mediaUrl, filename: file.name };
    };

    const uploadImage = async (file) => (await uploadMedia(file))?.url || null;

    // A wiki change resets the selection, unless a deep link named a page in
    // THIS wiki.
    //
    // ⚠️ THE RESET IS REPORTED UPWARDS TOO (§14A). The parent writes the
    // persisted state; without this, switching wiki would store the page id of
    // the wiki just left.
    useEffect(() => {
        setSelectedPageId(initialPageId || null);
        onPageSelect?.(initialPageId || null);
        setPage(null);
        setDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wikiId, initialPageId]);

    // ⚠️ `silent` (LIV, D1): the live path's reload of a CLEAN page. No error
    // state is touched - a transient failure on a request the reader never made
    // must not put "Failed to load the page" over a page that is still there.
    const loadPage = useCallback(async (pageId, { skipIfDirty = false, silent = false } = {}) => {
        if (!pageId) return;
        if (!silent) setLoadError(null);
        try {
            const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${pageId}`);
            if (!response.ok) throw new Error(`Failed to load the page (${response.status})`);
            const data = await response.json();
            // ⛔ RE-CHECK ON ARRIVAL. The clean test that let this request go out
            // happened BEFORE the round trip, so anything typed in between would
            // be overwritten here - and the trigger is the echo of the user's own
            // save, which means it recurs rather than happening once.
            if (skipIfDirty && liveStateRef.current.dirty) return;
            setPage(data);
            setTitle(data.title || '');
            setTags(data.tags || []);
            // ⚠️ SANITISED ON THE WAY OUT OF THE SERVER TOO (§7.1).
            const loaded = sanitiseWikiHtml(data.contentHtml || '');
            savedHtmlRef.current = loaded;
            setContentHtml(loaded);
            setDirty(false);

            const draft = readDraft({ sub: subRef.current, wikiId, pageId });
            setPendingDraft(draftIsNewerThan(draft, data.updatedAt) ? draft : null);
        } catch (err) {
            console.error('Failed to load a TME wiki page:', err);
            // A background reload keeps what is on screen and says why on the
            // console; the two branches below are for loads the reader asked for.
            if (silent) return;
            // ⚠️ §14A: a RESTORED page id may name a page that has since been
            // deleted, or one whose wiki no longer shows it. Arriving on an
            // error is a poor greeting, so an initial page that will not load
            // falls back to no selection; a page the reader CLICKED still
            // reports its failure.
            if (pageId === initialPageId) {
                setSelectedPageId(null);
                onPageSelect?.(null);
            } else {
                setLoadError(err.message);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wikiId, initialPageId]);

    useEffect(() => {
        loadPage(selectedPageId);
    }, [selectedPageId, loadPage]);

    /**
     * ⛔ EVERY DOOR THAT REPLACES OR DISCARDS THE OPEN DOCUMENT IS SHUT WHILE A
     * MEDIA DELETE IS IN FLIGHT (TWM §7.3.1, Fable's post-build review).
     *
     * The `readOnly` prop reaches the editor and the toolbar; LIV's three doors
     * were closed with `deletingMedia` when that work landed underneath TWM. The
     * class is WIDER than those four, and this is the rest of it, enumerated
     * from the code rather than from the plan:
     *
     *   selectPage    the tree - the one Fable found
     *   createPage    both "+" buttons, which then select the new page
     *   archivePage   the header button AND the tree's delete
     *   restoreDraft  the draft banner, which replaces the body outright
     *   discardDraft  ditto, by removing what the banner would restore
     *
     * Each of them, mid-flow, discards the pending removal - and if the
     * in-flight `save()` then fails, the restore would re-insert the image into
     * whatever document is open by then. (`restorePage` is not a member: it
     * renders only for an archived page, and the flow refuses those.)
     *
     * ⚠️ A REF, not the state value: `onDeleteMedia` is a stable callback and the
     * tree's handlers are re-created per render, so only the ref is current in
     * both.
     */
    const refuseWhileDeletingMedia = () => {
        if (!deletingMediaRef.current) return false;
        window.alert('A file is being deleted. Wait for that to finish.');
        return true;
    };

    const selectPage = (pageId) => {
        if (refuseWhileDeletingMedia()) return;
        if (dirty && !window.confirm('You have unsaved changes. Discard them and switch pages?')) {
            return;
        }
        setDirty(false);
        setSelectedPageId(pageId);
        // §14A: the parent holds the persisted state, so the selection travels
        // up as well as being kept here.
        onPageSelect?.(pageId);
    };

    // -----------------------------------------------------------------------
    // Live updates (LIV-14, D4). The PAGE handles the socket and forwards the
    // message; this pane owns the page list, the open page and its `version`,
    // so the refetch, the compare and the banner all live here.
    //
    // The two panels below fetch their OWN contents and are mounted inside this
    // pane, so they take a refresh key - the parent can reach neither.
    // -----------------------------------------------------------------------

    /** "Somebody else saved this page" - D4, the same banner as D3 and D9. */
    const [stalePage, setStalePage] = useState(false);
    useEffect(() => {
        if (!dirty) setStalePage(false);
    }, [dirty]);

    /** Bumped on every message; the links and version panels watch it. */
    const [panelRefreshKey, setPanelRefreshKey] = useState(0);

    // ⚠️ `deletingMedia` IS IN HERE TOO, and it is the one that is easy to miss
    // (TWM §7.3.1): gate 3's hold below reads `liveStateRef.current`, so without
    // it messages arriving during the delete flow are neither held nor visible
    // to it, and the other three guards are half-done.
    const liveStateRef = useRef({});
    liveStateRef.current = { page, dirty, saving, deletingMedia };

    // ⛔ EVERY HELD MESSAGE, NOT THE LAST ONE. A save takes seconds on a cold
    // container, and two messages inside that window - one for another page,
    // one for the open page - would have left only the second, dropping the
    // first outright.
    const pendingRef = useRef([]);

    const runLiveRefresh = useCallback(async (messages) => {
        // GATE 2 - this pane shows ONE wiki.
        const relevant = (messages || []).filter(message => message && message.wikiId === wikiId);
        if (relevant.length === 0) return;

        // GATE 0 lives HERE - at the point of refetch, after gate 2 - not per
        // message in the parent's socket handler (Fable, round 2). A session
        // that cannot be renewed puts the messages BACK for the next signal
        // (D11); a background request must never take apiAdmin's 401 path and
        // redirect a tab with an editor open in it.
        if (!await adminSessionUsable()) {
            pendingRef.current = [...relevant, ...pendingRef.current];
            return;
        }

        // The page list is the tree's data, and it is always safe to refresh:
        // it holds no typed text.
        fetchPages({ silent: true });
        setPanelRefreshKey(key => key + 1);

        const { page: openPage, dirty: isDirty } = liveStateRef.current;
        if (!openPage) return;
        // A message naming another page in this wiki has already refreshed the
        // tree; the open page is untouched by it. A WIKI-level message (no
        // pageId) touches everything.
        const touchesOpenPage = relevant.some(
            message => !message.pageId || message.pageId === openPage.pageId);
        if (!touchesOpenPage) return;

        if (!isDirty) {
            // Clean: silently reload the page (D4). A reload to the value we
            // already hold is invisible - and `skipIfDirty` stops a late
            // response overwriting keystrokes typed while it was in flight.
            loadPage(openPage.pageId, { skipIfDirty: true, silent: true });
            return;
        }

        // ⛔ DIRTY: the typed text is never touched. Ask what the server holds
        // and compare.
        //
        // ⚠️ `page.version` - THE OPTIMISTIC-LOCK COUNTER - and never
        // `currentVersion`. That one is the version-HISTORY number and only
        // advances on a BODY change, so a tag-only or title-only save by
        // another admin would compare equal and be swallowed as an echo of our
        // own write. The two sit side by side in the same projection, which is
        // exactly how the wrong one gets picked.
        try {
            const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${openPage.pageId}`);
            if (!response.ok) return;
            const data = await response.json();
            if ((data.version || 0) > (openPage.version || 0)) setStalePage(true);
        } catch (err) {
            console.error('Failed to check for a newer TME wiki page:', err);
        }
    }, [wikiId, fetchPages, loadPage]);

    // Everything above the last sequence this pane saw.
    //
    // ⚠️ SEEDED FROM THE QUEUE AT MOUNT, not from zero: the parent keeps up to
    // fifty entries, and a pane mounting fresh (the first wiki selected after
    // messages arrived) would replay all of them against its own mount fetch
    // (Fable, round 2). What arrived before the mount is what the mount fetched.
    const lastSeqRef = useRef(liveMessages.length ? liveMessages[liveMessages.length - 1].seq : 0);
    useEffect(() => {
        const fresh = liveMessages.filter(entry => entry.seq > lastSeqRef.current);
        if (fresh.length === 0) return;
        lastSeqRef.current = fresh[fresh.length - 1].seq;
        for (const { message } of fresh) pendingRef.current.push(message);

        // GATE 3, PANE HALF: `saving` gates this pane's own refetch, never the
        // compare - the compare is what raises the banner, and a save in flight
        // is precisely when somebody else's write matters. Held, not dropped.
        //
        // ⚠️ AND `deletingMedia`, which is what puts that field in
        // `liveStateRef` (TWM §7.3.1). The delete flow's window is WIDER than
        // its save: between save() resolving and the DELETE returning, `saving`
        // is already false, so a message arriving in there would drain straight
        // through this hold and replace the document mid-flow.
        if (liveStateRef.current.saving || liveStateRef.current.deletingMedia) return;
        const held = pendingRef.current;
        pendingRef.current = [];
        runLiveRefresh(held);
    }, [liveMessages, runLiveRefresh]);

    // ⛔ `deletingMedia` GATES THIS DRAIN TOO (TWM §7.3.1, P1). Keyed on
    // `saving` alone it fires the INSTANT save() resolves - while the delete
    // flow is still between its save and its DELETE. The page is not dirty by
    // then, so runLiveRefresh reloads it silently and the pending removal is
    // discarded; the flow would then delete the object and leave a live page
    // pointing at bytes that are gone.
    //
    // ⚠️ HELD, NEVER DROPPED - the messages stay in pendingRef, exactly as they
    // do for `saving`, so the refresh happens the moment the flow ends.
    useEffect(() => {
        if (saving || deletingMedia || pendingRef.current.length === 0) return;
        const held = pendingRef.current;
        pendingRef.current = [];
        runLiveRefresh(held);
    }, [saving, deletingMedia, runLiveRefresh]);

    /**
     * D4's Reload: discard the local edits and take the server's copy.
     *
     * ⚠️ THE LOCALSTORAGE DRAFT GOES TOO. The editor autosaves one keyed by the
     * admin's Cognito sub, and a reload that left it behind would offer the
     * discarded text back on the next open - so the Reload would appear not to
     * have worked.
     */
    const handleStaleReload = useCallback(async () => {
        const { page: openPage } = liveStateRef.current;
        if (!openPage) return;
        setStalePage(false);
        clearDraft({ sub: subRef.current, wikiId, pageId: openPage.pageId });
        setPendingDraft(null);
        setDirty(false);
        // Deliberately NOT skipIfDirty: Reload's whole job is to discard.
        await Promise.all([fetchPages(), loadPage(openPage.pageId)]);
    }, [wikiId, fetchPages, loadPage]);

    // The LATEST title and body, for the draft writers below. Both write from
    // here rather than from a closure, so the one that fires later can never
    // overwrite a newer body with the stale one it captured (review finding
    // 13).
    const latestRef = useRef({ title: '', contentHtml: '' });
    useEffect(() => {
        latestRef.current = { title, contentHtml };
    }, [title, contentHtml]);

    const writeDraft = useCallback(() => {
        saveDraft({ sub: subRef.current, wikiId, pageId: selectedPageId, ...latestRef.current });
    }, [wikiId, selectedPageId]);

    // The title's own debounce. The editor already debounces the body before
    // it reaches onEditorChange; the title arrived per keystroke, and a draft
    // is up to 350KB.
    const titleDraftTimer = useRef(null);
    useEffect(() => () => {
        if (titleDraftTimer.current) clearTimeout(titleDraftTimer.current);
    }, []);

    const onEditorChange = (html) => {
        setContentHtml(html);
        latestRef.current = { ...latestRef.current, contentHtml: html };
        // The debounced echo of a body that has just been saved (or just been
        // loaded) is not an edit: it must neither re-enable Save nor write a
        // draft. Judged on the ref alone - the editor's timer captured THIS
        // function at the previous render, so `dirty` in here can be stale. A
        // dirty title has already set `dirty` by its own path and is untouched.
        if (html === savedHtmlRef.current) return;
        setDirty(true);
        // Autosave goes to localStorage ONLY (D3): only an explicit Save writes
        // a version, so an autosave that reached the server would either write a
        // version nobody asked for or store a body with none.
        writeDraft();
    };

    const onTitleChange = (value) => {
        setTitle(value);
        setDirty(true);
        latestRef.current = { ...latestRef.current, title: value };
        if (titleDraftTimer.current) clearTimeout(titleDraftTimer.current);
        titleDraftTimer.current = setTimeout(writeDraft, TITLE_DRAFT_DEBOUNCE_MS);
    };

    /**
     * ⚠️ IT REPORTS ITS OUTCOME: `true` on success, `false` on EVERY refusal
     * (TWM §7.3). It used to return `undefined` on all four paths - it alerts
     * and returns on a non-OK response, and only a NETWORK error throws - so
     * "a failed save stops the delete" could not be built at all.
     *
     * ⚠️ ALL FOUR EXITS, and `if (!page)` is the one that matters most: it is
     * the exact state a stale callback produces (§7.1.1), so leaving it
     * `undefined` would let that defect read as a successful save and issue the
     * DELETE anyway. Everything else here - the alerts, the 409 reload, the
     * merge - is unchanged.
     */
    const save = async () => {
        if (!page) return false;
        setSaving(true);
        try {
            // ⚠️ THE LIVE DOCUMENT, NOT THE STATE (TWS §3.2). `contentHtml`
            // lags the editor by the editor's debounce, so an edit made just
            // before the click was not in the PUT.
            const html = editorRef.current.getHTML();
            const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${page.pageId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version: page.version, title, contentHtml: html })
            });
            const data = await response.json();
            if (!response.ok) {
                // A 409 names the conflict (§6.8). Reload rather than let the
                // next save overwrite somebody else's work.
                window.alert(data.message || 'Could not save the page');
                if (response.status === 409) await loadPage(page.pageId);
                return false;
            }
            // ⚠️ MERGE THE ROW, NEVER RELOAD THE PAGE (TWS §3.1). The first
            // write per container takes ~7 s; reloading here replaced the
            // editor body with the stored one and discarded every edit made
            // while waiting - silently, with Save gone grey. Same shape as the
            // tag save below.
            // `contentHtml` is deliberately NOT set here: the editor's sync
            // effect resets the document whenever `content` differs from it,
            // so writing the sent html back would discard whatever was typed
            // during the wait - the very thing this change removes. State only
            // ever follows the editor, through onEditorChange.
            savedHtmlRef.current = html;
            setPage(prev => ({ ...prev, ...data }));
            clearDraft({ sub: subRef.current, wikiId, pageId: page.pageId });
            setPendingDraft(null);
            // Edits made while the PUT was in flight are still in the editor
            // and still unsaved: they keep the page dirty and get a fresh draft.
            const live = editorRef.current.getHTML();
            const stillDirty = live !== html || latestRef.current.title !== title;
            setDirty(stillDirty);
            if (stillDirty) {
                latestRef.current = { ...latestRef.current, contentHtml: live };
                writeDraft();
            }
            await fetchPages();
            return true;
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------------------------------------
    // Deleting a media file (TWM §7.3). The image's bin and the attachment's
    // toolbar button both arrive here; this owns the WHOLE sequence, including
    // removing the node, because nothing must disappear if the user cancels.
    // -----------------------------------------------------------------------

    /**
     * ⛔ SAVE THE PAGE FIRST, THEN DELETE THE OBJECT (§5.1). This order is not a
     * preference, it is the failure direction: reversed, a failure between the
     * two steps leaves the live page pointing at bytes that no longer exist - a
     * broken image on a page nobody edited. In this order the same failure
     * leaves an unreferenced object, which costs a fraction of a penny and is
     * exactly what the clean-up script exists to reclaim.
     */
    const onDeleteMedia = async ({ src, node, getPos, deleteNode, link }) => {
        // ⛔ NO RE-ENTRY. Read from the REF, not the state value: this callback
        // is stable (§7.1.1), so a captured render's `deletingMedia` would be
        // stale exactly when it matters. Two confirms stacked on one in-flight
        // save means two save()s racing on one page.version.
        if (deletingMediaRef.current) return;
        if (!page) return;

        const editor = editorRef.current;
        if (!editor) return;

        // ⚠️ A TME image whose src is not a TME media path was never in our
        // bucket - a pasted external image - so offering to delete it would be a
        // lie. It is simply removed, HERE: the node view hands the whole
        // sequence over, so an early return that did not call deleteNode() would
        // leave the bin doing nothing at all.
        if (typeof src !== 'string' || !src.startsWith('/tme-wiki-media/')) {
            deleteNode();
            return;
        }

        // ⛔ THE LAST PATH SEGMENT, never "everything after the wiki id" (J1).
        // An attachment's href is /tme-wiki-media/{wikiId}/docs/doc_x.pdf, so
        // "everything after {wikiId}/" yields `docs/doc_x.pdf` and the request
        // becomes …/media/docs/doc_x.pdf - THREE segments after `media`,
        // matching neither route. API Gateway answers 403 and attachments are
        // dead.
        const parts = src.split('/').filter(Boolean);   // ['tme-wiki-media', wikiId, …]
        const mediaId = parts[parts.length - 1];
        const srcWikiId = parts[1];
        const middle = parts.slice(2, -1);              // [] or ['docs']

        // Cross-wiki illustration is legitimate (D19), but deleting another
        // wiki's file from this page's editor is not. Refused locally, with no
        // request - as is any shape this editor never authored.
        if (srcWikiId !== wikiId) {
            window.alert('That file belongs to another wiki. Delete it from that wiki\'s page.');
            return;
        }
        if (!(middle.length === 0 || (middle.length === 1 && middle[0] === 'docs'))) {
            window.alert('That is not a file this editor uploaded, so it cannot be deleted here.');
            return;
        }

        const mediaPath = `${API}/wikis/${wikiId}/pages/${page.pageId}/media/${mediaId}`;

        // ⚠️ IF THE USAGE CALL FAILS, THE FLOW ABORTS. It never continues with a
        // count of 0: that would silently drop the D4 warning and present a
        // destructive action as a safe one. Absence is a failure, not a zero.
        let versionCount;
        try {
            const response = await apiFetch(`${mediaPath}/usage`);
            if (!response.ok) throw new Error(`usage failed (${response.status})`);
            const data = await response.json();
            versionCount = data.versionCount;
        } catch (err) {
            console.error('Failed to check where a TME wiki media file is used:', err);
            window.alert('Could not check where this file is used. Nothing was deleted.');
            return;
        }

        const historyLine = versionCount > 0
            ? `\n\n${versionCount} saved version${versionCount === 1 ? '' : 's'} of this page `
                + `still use${versionCount === 1 ? 's' : ''} it. Rolling back to `
                + `${versionCount === 1 ? 'it' : 'one of them'} will show a broken image.`
            : '';
        // A blocking window.confirm, matching every other confirmation in this
        // pane. The anchored non-blocking card the Gantt uses exists because a
        // DRAG has no other way to ask.
        if (!window.confirm(
            `Delete this file permanently?${historyLine}\n\n`
            + 'This will save the page. Deleted files can be recovered from AWS for 30 days.'
        )) return;

        // ⛔ CAPTURE ENOUGH TO PUT IT BACK EXACTLY, and never editor.commands.undo().
        // ⚠️ Typing is locked out for THIS flow (the readOnly prop below), but
        // undo() is still the wrong instrument: it reverts the last history
        // GROUP, and TipTap's 500 ms newGroupDelay can have merged the removal
        // into a keystroke made just before the bin was pressed - so one undo()
        // reverts both or neither. An explicit capture and re-insert says what it
        // means. (An ordinary Save is different: it does NOT lock the editor, and
        // TWS deliberately supports typing through its ~7 s first write.)
        const restore = link
            ? { pos: link.from, json: { type: 'text', text: link.text, marks: [{ type: 'link', attrs: { href: link.href } }] } }
            : { pos: getPos(), json: node.toJSON() };
        // ⚠️ WHICH PAGE THIS WAS. `refuseWhileDeletingMedia` shuts every door
        // this pane owns, but the PARENT can still switch the wiki or the page
        // through its props - and re-inserting into a document that is no longer
        // the one the image came from would put it on the WRONG PAGE.
        const restorePageId = page.pageId;

        deletingMediaRef.current = true;
        setDeletingMedia(true);
        try {
            deleteNode();
            if (!(await save())) {
                // ⚠️ ONLY IF IT IS ACTUALLY GONE (§7.3.2). save()'s 409 branch
                // awaits loadPage, and the editor's sync effect then replaces
                // the whole document with the server's body - image included -
                // while save() still answers false. An unconditional insert
                // would add a SECOND copy, at a position measured against a
                // document that no longer exists. Whether that sync fires at all
                // depends on two serialisations being string-identical, so the
                // bug would be nondeterministic.
                if (liveStateRef.current.page?.pageId === restorePageId
                    && !editor.getHTML().includes(src)) {
                    editor.commands.insertContentAt(restore.pos, restore.json);
                }
                return;                            // no DELETE issued
            }

            const response = await apiFetch(mediaPath, { method: 'DELETE' });
            if (!response.ok) {
                // ⛔ SHOW THE SERVER'S OWN WORDS, and only promise the sweep when
                // the sweep is actually the answer. A 409 means something still
                // REFERENCES the file - the server names the page, and the sweep
                // deliberately KEEPS a referenced object, so telling the operator
                // it will be reclaimed is false and unactionable. The commonest
                // way to reach it is the same image pasted twice on one page:
                // one copy is removed, the save keeps the other, and the refusal
                // names this very page.
                const data = await response.json().catch(() => ({}));
                const reason = data.message || `HTTP ${response.status}`;
                window.alert(
                    `The page was saved, but the file was not deleted.\n\n${reason}`
                    + (response.status === 409
                        ? ''
                        : '\n\nIt will be reclaimed by the clean-up script.')
                );
            }
        } finally {
            deletingMediaRef.current = false;
            setDeletingMedia(false);
        }
    };

    /**
     * Tags are saved on their OWN route (§12.2): a re-tagging is
     * `tme_wiki_tags_changed`, not an edit, so the Audit page can tell them
     * apart - and a tag change is not a body change, so it writes no version.
     */
    const saveTags = async (next) => {
        const previous = tags;
        setTags(next);
        if (!page) return;
        const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${page.pageId}/tags`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ version: page.version, tags: next })
        });
        const data = await response.json();
        if (!response.ok) {
            window.alert(data.message || 'Could not save the tags');
            setTags(previous);
            if (response.status === 409) {
                // Somebody else changed the page, so it has to be reloaded - but
                // an unsaved body must stay reachable through the draft banner,
                // which only offers a draft NEWER than the page. Re-stamp it.
                if (dirty) writeDraft();
                await loadPage(page.pageId);
            }
            return;
        }
        // ⚠️ MERGE THE ROW, NEVER RELOAD THE PAGE (review finding 7). A tag save
        // bumps `version` and `updatedAt` on the server. Reloading here reset the
        // body to the stored one and cleared `dirty`, silently discarding an
        // unsaved edit - and because the draft was now OLDER than `updatedAt`,
        // the restore banner never offered it back. The response carries the
        // fields that changed; only those move.
        setPage(prev => ({
            ...prev,
            tags: data.tags,
            tagKeys: data.tagKeys,
            version: data.version,
            updatedAt: data.updatedAt
        }));
        setTags(data.tags || next);
        // A draft written before this save is older than the page now; a dirty
        // body is re-stamped so a later reopen still offers it.
        if (dirty) writeDraft();
        await Promise.all([fetchPages(), fetchTags()]);
    };

    const createPage = async (parentId = null) => {
        if (refuseWhileDeletingMedia()) return;
        const newTitle = window.prompt('Page title');
        if (!newTitle || !newTitle.trim()) return;
        const response = await apiFetch(`${API}/wikis/${wikiId}/pages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: newTitle.trim(), parentId, contentHtml: '' })
        });
        const data = await response.json();
        if (!response.ok) {
            window.alert(data.message || 'Could not create the page');
            return;
        }
        await fetchPages();
        setSelectedPageId(data.pageId);
    };

    const archivePage = async (pageId) => {
        if (refuseWhileDeletingMedia()) return;
        const target = pages.find(p => p.pageId === pageId);
        if (!target) return;
        if (!window.confirm(`Archive "${target.title}"? It can be restored.`)) return;

        const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${pageId}/archive`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ version: target.version })
        });
        const data = await response.json();
        if (!response.ok) {
            window.alert(data.message || 'Could not archive the page');
            return;
        }
        // ⚠️ INBOUND LINKS ARE REPORTED, NOT REFUSED (D10). A live page may point
        // at an archived one; the reader is told, and decides.
        if (data.inboundLinks?.length) {
            window.alert(
                `Archived. ${data.inboundLinks.length} page(s) still link here:\n`
                + data.inboundLinks.map(l => `• ${l.pageTitle}`).join('\n')
            );
        }
        await fetchPages();
        if (pageId === selectedPageId) await loadPage(pageId);
    };

    const restorePage = async () => {
        if (!page) return;
        const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${page.pageId}/restore`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ version: page.version })
        });
        const data = await response.json();
        if (!response.ok) {
            // Refuses under an archived wiki or an archived parent - the message
            // says which.
            window.alert(data.message || 'Could not restore the page');
            return;
        }
        await Promise.all([fetchPages(), loadPage(page.pageId)]);
    };

    const reorderPage = async (draggedId, newParentId, newOrder) => {
        const target = pages.find(p => p.pageId === draggedId);
        if (!target) return;
        const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${draggedId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ version: target.version, parentId: newParentId || null, order: newOrder })
        });
        if (!response.ok) {
            const data = await response.json();
            // A move into a descendant, or under an archived parent, is refused
            // by name (§6.6).
            window.alert(data.message || 'Could not move the page');
        }
        await fetchPages();
    };

    /**
     * The editor's inline buttons open the SAME picker the panel uses, and the
     * node is inserted ONLY after the row is written (§9.3, D5, H4).
     *
     * ⚠️ ROW FIRST, NODE SECOND, and the order is the point. A node in the body
     * that no link row knows about is precisely the drift D5 exists to prevent:
     * the panel would not list it, and "Used by TME Wiki" would not find it.
     * The reverse - a row with no node - is harmless and self-correcting,
     * because the panel shows it and it is removed there.
     */
    const startInlineInsert = (kind) => {
        setInsertOnLink(true);
        setPicker(kind);
    };

    const onLinked = (link) => {
        if (!insertOnLink) return;
        setInsertOnLink(false);
        const editor = editorRef.current;
        if (!editor) return;

        if (link.kind === 'script') {
            editor.commands.insertScriptRef({
                scriptId: link.targetId, label: link.targetLabel
            });
        } else if (link.kind === 'page') {
            const [targetWikiId, targetPageId] = String(link.targetId).split('#');
            editor.commands.insertTmeWikiPageLink({
                wikiId: targetWikiId, pageId: targetPageId, label: link.targetLabel
            });
        }
        setDirty(true);
    };

    const restoreDraft = () => {
        if (refuseWhileDeletingMedia()) return;
        setTitle(pendingDraft.title || '');
        setContentHtml(sanitiseWikiHtml(pendingDraft.contentHtml || ''));
        setDirty(true);
        setPendingDraft(null);
    };

    const discardDraft = () => {
        if (refuseWhileDeletingMedia()) return;
        clearDraft({ sub: subRef.current, wikiId, pageId: selectedPageId });
        setPendingDraft(null);
    };

    // Two tags NARROW (AND), matching what the server's tag search does - so the
    // filter here and a tag search there cannot mean different things.
    const filterKeys = (tagFilter || []).map(normaliseTag).filter(Boolean);
    const treePages = pages
        .filter(p => showArchivedPages || !p.archivedAt)
        .filter(p => filterKeys.length === 0
            || filterKeys.every(key => (p.tags || []).map(normaliseTag).includes(key)));
    const archived = Boolean(page?.archivedAt);

    return (
        <div className="h-full flex overflow-hidden">
            <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
                <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 truncate">
                        {wiki?.name || 'Pages'}
                    </span>
                    <button
                        type="button"
                        onClick={() => createPage(null)}
                        className="text-primary-600 hover:text-primary-800"
                        title="New top-level page"
                    >
                        <FaPlus className="w-3 h-3" />
                    </button>
                </div>
                <label className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600 border-b border-gray-100">
                    <input
                        type="checkbox"
                        checked={showArchivedPages}
                        onChange={(e) => setShowArchivedPages(e.target.checked)}
                    />
                    Show archived pages
                </label>
                <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Filter by tag</p>
                    <TagInput
                        value={tagFilter}
                        onChange={onTagFilterChange}
                        suggestions={tagSuggestions}
                        placeholder="Tag…"
                    />
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                    <WikiTreeView
                        pages={treePages}
                        selectedPageId={selectedPageId}
                        onSelect={selectPage}
                        onReorder={reorderPage}
                        onAddChild={(parentId) => createPage(parentId)}
                        onDelete={archivePage}
                        storageKey={`tmeWikiTree_${wikiId}`}
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                {loadError && <p className="text-sm text-red-600 mb-3">{loadError}</p>}

                {!page && !loadError && (
                    <p className="text-gray-500">Select a page, or create one.</p>
                )}

                {page && (
                    <>
                        {archived && (
                            <div className="mb-3 flex items-center justify-between rounded border border-amber-300 bg-amber-50 px-3 py-2">
                                <span className="text-sm text-amber-800">
                                    Archived {page.archivedAt?.slice(0, 10)}. This page is read-only.
                                </span>
                                <button
                                    type="button"
                                    onClick={restorePage}
                                    className="flex items-center gap-1 text-sm text-primary-700 hover:text-primary-900"
                                >
                                    <FaUndo className="w-3 h-3" />
                                    Restore
                                </button>
                            </div>
                        )}

                        {pendingDraft && !archived && (
                            <div className="mb-3 flex items-center justify-between rounded border border-blue-300 bg-blue-50 px-3 py-2">
                                <span className="text-sm text-blue-800">
                                    Unsaved changes from {pendingDraft.savedAt?.slice(0, 16).replace('T', ' ')}
                                </span>
                                <span className="flex gap-3">
                                    <button type="button" onClick={restoreDraft} className="text-sm text-primary-700 hover:text-primary-900">
                                        Restore unsaved changes
                                    </button>
                                    <button type="button" onClick={discardDraft} className="text-sm text-gray-600 hover:text-gray-800">
                                        Discard
                                    </button>
                                </span>
                            </div>
                        )}

                        <div className="flex items-center gap-3 mb-3">
                            <input
                                type="text"
                                aria-label="Page title"
                                value={title}
                                maxLength={200}
                                disabled={archived}
                                onChange={(e) => onTitleChange(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded text-lg font-semibold disabled:bg-gray-100"
                            />
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving || archived || !dirty}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50"
                            >
                                <FaSave className="w-3 h-3" />
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                            {!archived && (
                                <button
                                    type="button"
                                    onClick={() => archivePage(page.pageId)}
                                    className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-red-600"
                                    title="Archive this page"
                                >
                                    <FaArchive className="w-3 h-3" />
                                </button>
                            )}
                        </div>

                        <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">Tags</p>
                            <TagInput
                                value={tags}
                                onChange={saveTags}
                                suggestions={tagSuggestions}
                                disabled={archived}
                            />
                        </div>

                        {/* D4: saved elsewhere while this page was open and
                            dirty. The typed text stays exactly as it is. */}
                        {stalePage && (
                            <StaleContentBanner
                                message="This page was saved somewhere else. Your changes are still here."
                                onReload={handleStaleReload}
                                confirm="You have unsaved changes. Discard them and load the newer page?"
                                // ⚠️ AND `deletingMedia` (TWM §7.3.1). Gated on
                                // `saving` alone this Reload is re-enabled the
                                // moment save() resolves - mid-flow - and
                                // handleStaleReload does setDirty(false) and a
                                // BARE loadPage(), deliberately without
                                // skipIfDirty, because discarding is its job.
                                busy={saving || deletingMedia}
                            />
                        )}

                        <TmeWikiPageEditor
                            content={contentHtml}
                            editorRef={editorRef}
                            onChange={onEditorChange}
                            onImageUpload={uploadImage}
                            onDocumentUpload={uploadMedia}
                            onInsertScriptRef={() => startInlineInsert('script')}
                            onInsertPageLink={() => startInlineInsert('page')}
                            onDeleteMedia={onDeleteMedia}
                            // ⛔ THE LOCK IS THIS PROP, not editor.setEditable()
                            // (TWM §7.3.1). setEditable stops typing and nothing
                            // else: the toolbar renders on this prop, so Undo,
                            // Redo and every other button would stay live for the
                            // whole ~7 s save. Pressing Undo mid-flow reverts the
                            // removal, the save then persists the image, the
                            // DELETE 409s on the server's wide guard, and the UI
                            // reports "the page was saved, but the file could not
                            // be deleted" - true, misleading and impossible to
                            // act on.
                            readOnly={archived || deletingMedia}
                        />

                        <TmeWikiLinksPanel
                            wikiId={wikiId}
                            refreshKey={panelRefreshKey}
                            page={page}
                            pages={pages}
                            scripts={scripts}
                            onRefreshScripts={onRefreshScripts}
                            expanded={linksExpanded}
                            onToggle={() => setLinksExpanded(v => !v)}
                            picker={picker}
                            onPickerChange={(kind) => {
                                if (kind === null) setInsertOnLink(false);
                                setPicker(kind);
                            }}
                            onLinked={onLinked}
                        />

                        {/* A rollback rewrites the page, so the tree, the body and
                            the version list all have to come back afterwards -
                            the same three reads a save does. */}
                        <VersionHistoryPanel
                            wikiId={wikiId}
                            refreshKey={panelRefreshKey}
                            page={page}
                            expanded={versionsExpanded}
                            onToggle={() => setVersionsExpanded(v => !v)}
                            // ⚠️ AND `deletingMedia` (TWM §7.3.1). A rollback is
                            // the third door LIV opened into the open document:
                            // onRolledBack does setDirty(false) + loadPage(), so
                            // firing it mid-flow discards the pending removal and
                            // the flow then deletes the object anyway.
                            readOnly={archived || deletingMedia}
                            onRolledBack={async () => {
                                clearDraft({ sub: subRef.current, wikiId, pageId: page.pageId });
                                setPendingDraft(null);
                                setDirty(false);
                                await Promise.all([fetchPages(), loadPage(page.pageId)]);
                            }}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default TmeWikiEditorPane;
